/**
 * ProductDetailsSticky
 * Handles dynamic sticky positioning for product details column
 * Allows scrolling through tall content by adjusting bottom position based on scroll direction
 */
class ProductDetailsSticky extends HTMLElement {
  constructor() {
    super();

    this.scrollValue = 0;
    this.scrollCatch = 0;
    this.isScrollingUp = false;
    this.scrollDelta = 0;
  }

  connectedCallback() {
    // Wait for the DOM to be fully loaded and painted
    // Use a longer delay to ensure all content is rendered
    setTimeout(() => {
      this.calculateScrollDelta();
      this.handleScroll = this.handleScroll.bind(this);
      this.handleResize = this.handleResize.bind(this);

      window.addEventListener('scroll', this.handleScroll, { passive: true });
      window.addEventListener('resize', this.handleResize, { passive: true });

      // Initial calculation
      this.calculateScrollCatch();
    }, 100);
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this.handleScroll);
    window.removeEventListener('resize', this.handleResize);
  }

  calculateScrollDelta() {
    // Calculate how much we can scroll the sticky content
    // This should be the difference between content height and the available sticky space

    let contentElement = this.querySelector('.group-block');

    if (!contentElement) {
      contentElement = this.querySelector('[class*="group"]');
    }

    if (!contentElement) {
      contentElement = this;
    }

    // Get the content height
    const contentHeight = contentElement.scrollHeight;

    // Get the viewport height minus header offset
    const viewportHeight = window.innerHeight;
    const headerOffset = parseInt(getComputedStyle(this).getPropertyValue('--sticky-header-offset')) || 0;
    const availableHeight = viewportHeight - headerOffset;

    // The scroll delta is how much content overflows the available space
    // If content is 1500px and viewport is 900px, we need to be able to scroll 600px
    this.scrollDelta = Math.max(0, contentHeight - availableHeight);

    // If content doesn't need scrolling, show a helpful message
    if (this.scrollDelta === 0) {
      console.warn(
        '⚠️ Sticky scroll disabled: Content (%dpx) fits within viewport (%dpx). Add more content to enable scrolling.',
        contentHeight,
        availableHeight
      );
    } else {
      console.log('✅ Sticky scroll enabled: Content can scroll %dpx', this.scrollDelta);
    }
  }

  handleResize() {
    this.calculateScrollDelta();
    this.calculateScrollCatch();
  }

  handleScroll() {
    // Recalculate scroll delta in case content height changed (accordions opened, etc.)
    this.calculateScrollDelta();
    this.calculateScrollCatch();
  }

  calculateScrollCatch() {
    const scrollTop = document.documentElement.scrollTop || document.body.scrollTop;
    const isScrollingUp = scrollTop <= this.scrollValue;

    // Reset when near top and direction changes
    if (this.isScrollingUp !== isScrollingUp && !isScrollingUp && scrollTop < 40) {
      this.scrollCatch = 0;
      this.style.setProperty('--scroll-catch', `${this.scrollCatch}px`);
      this.isScrollingUp = isScrollingUp;
      this.scrollValue = scrollTop;
      return;
    }

    if (isScrollingUp) {
      // Scrolling up - decrease scroll catch (more negative)
      const delta = this.scrollValue - scrollTop;
      this.scrollCatch = Math.max(this.scrollCatch - delta, -this.scrollDelta);
      this.style.setProperty('--scroll-catch', `${this.scrollCatch}px`);
    } else if (!isScrollingUp) {
      // Scrolling down
      if (Math.abs(this.scrollCatch) > 0) {
        // Only increase if we have negative scroll catch and passed the threshold
        if (scrollTop > this.scrollDelta) {
          const delta = scrollTop - this.scrollValue;
          this.scrollCatch = Math.min(this.scrollCatch + delta, 0);
          this.style.setProperty('--scroll-catch', `${this.scrollCatch}px`);
        }
      }
    }

    this.isScrollingUp = isScrollingUp;
    this.scrollValue = scrollTop;
  }
}

customElements.define('product-details-sticky', ProductDetailsSticky);
